import type { IssueNavigationIntent } from '@/features/audit-issues/types';

/**
 * Correction Request DTOs.
 *
 * Mirrors `backend/src/modules/correction_requests/model.rs` (SPEC v1.0 §17.2).
 * Locked / confirmed data may only be changed through a CorrectionRequest.
 *
 * Lifecycle: requested → approved → applied. A 'requested' row may instead be
 * rejected; 'voided' is a terminal state used by the backend. JSON value
 * fields (`requested_change` / `old_value` / `new_value`) are free-form jsonb.
 */

export const correctionStatusValues = [
  'requested',
  'approved',
  'rejected',
  'applied',
  'voided',
] as const;
export type CorrectionStatus = (typeof correctionStatusValues)[number];

/** Response DTO for a correction request (`CorrectionRequestDto`). */
export interface CorrectionRequestDto {
  id: string;
  monthly_batch_id: string | null;
  target_entity_type: string;
  target_entity_id?: string | null;
  requested_change: unknown | null;
  old_value: unknown | null;
  new_value: unknown | null;
  field_changes?: CorrectionFieldChange[];
  reason: string;
  requested_by: string | null;
  reviewed_by: string | null;
  status: CorrectionStatus;
  created_at: string;
  reviewed_at: string | null;
  updated_at: string;
  /** Human-readable identity of the target record (lot code, date, …). Never a raw UUID (spec-13 §8.8). */
  target_label: string | null;
  /** `year_month / facility` display of the owning monthly summary sheet. */
  batch_display: string | null;
  /** Typed allowlisted navigation intent for the target (round-6 item 6). */
  navigation?: IssueNavigationIntent | null;
  /** Route availability AND the viewer's authorization for `navigation`. */
  can_open_target?: boolean;
}

/** Value kind a correction field accepts (drives the client control, spec-13 §8.8). */
export type CorrectionFieldKind = 'number' | 'text' | 'date' | 'flag';

/** One correctable field on an entity type, from `GET /correction-requests/schema`. */
export interface CorrectionFieldSpec {
  key: string;
  kind: CorrectionFieldKind;
  clearable: boolean;
}

/** Server-derived before/after projection for ordinary users. */
export interface CorrectionFieldChange {
  field: string;
  before: unknown;
  after: unknown;
}

/** One correctable entity type and its field allowlist. */
export interface CorrectionTypeSpec {
  entity: string;
  fields: CorrectionFieldSpec[];
}

/** One authorized, batch-scoped target option from `GET /correction-requests/targets`. */
export interface CorrectionTargetOption {
  target_entity_id: string;
  label: string;
  batch_display: string | null;
  status: string | null;
}

/** Request body for `POST /api/correction-requests`. */
export interface CreateCorrectionRequest {
  monthly_batch_id?: string | null;
  target_entity_type: string;
  target_entity_id: string;
  requested_change?: unknown | null;
  old_value?: unknown | null;
  new_value?: unknown | null;
  reason: string;
}

/** Query filter for the list endpoint. */
export interface CorrectionListQuery {
  status?: CorrectionStatus;
  monthly_batch_id?: string;
}
